import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react'
import { BreadboardCanvas } from '@/components/BreadboardCanvas'
import { Inspector } from '@/components/Inspector'
import { Palette } from '@/components/Palette'
import { ProjectDialog } from '@/components/ProjectDialog'
import { Toolbar } from '@/components/Toolbar'
import { parseDocument, serializeDocument } from '@/domain/document'
import { projectApi } from '@/services/api'
import { requireCloudToken } from '@/services/auth'
import { CircuitJsEngine } from '@/services/CircuitJsEngine'
import { useWorkbenchStore } from '@/store/useWorkbenchStore'

const DRAFT_KEY = 'cycore_breadboard_draft_v1'
const WORKSPACE_KEY = 'cycore_breadboard_workspace_v1'
const THEME_KEY = 'darkMode'
type Theme = 'dark' | 'light'

interface PersistedWorkspace {
  projectId: number | null
  document: unknown
  dirty?: boolean
}

function persistCurrentWorkspace(): void {
  const state = useWorkbenchStore.getState()
  const payload = JSON.stringify({
    projectId: state.projectId,
    document: JSON.parse(serializeDocument(state.document)),
    dirty: state.dirty,
  } satisfies PersistedWorkspace)
  localStorage.setItem(WORKSPACE_KEY, payload)
  if (state.dirty) localStorage.setItem(DRAFT_KEY, payload)
  else localStorage.removeItem(DRAFT_KEY)
}

export default function App() {
  const [theme, setTheme] = useState<Theme>(() => localStorage.getItem(THEME_KEY) === 'false' ? 'light' : 'dark')
  const [dialog, setDialog] = useState<'open' | 'saveAs' | null>(null)
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; message: string } | null>(null)
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false)
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(false)
  const [fullscreenSnapshot, setFullscreenSnapshot] = useState<{ left: boolean; right: boolean } | null>(null)
  const workspaceHydratedRef = useRef(false)
  const restoredWorkspaceRef = useRef(false)
  const canvasFullscreen = fullscreenSnapshot !== null
  const queryClient = useQueryClient()

  const toggleCanvasFullscreen = useCallback(() => {
    if (fullscreenSnapshot) {
      setLeftSidebarCollapsed(fullscreenSnapshot.left)
      setRightSidebarCollapsed(fullscreenSnapshot.right)
      setFullscreenSnapshot(null)
    } else {
      setFullscreenSnapshot({ left: leftSidebarCollapsed, right: rightSidebarCollapsed })
      setLeftSidebarCollapsed(true)
      setRightSidebarCollapsed(true)
    }
  }, [fullscreenSnapshot, leftSidebarCollapsed, rightSidebarCollapsed])

  const document = useWorkbenchStore((state) => state.document)
  const projectId = useWorkbenchStore((state) => state.projectId)
  const dirty = useWorkbenchStore((state) => state.dirty)
  const running = useWorkbenchStore((state) => state.running)
  const closedContacts = useWorkbenchStore((state) => state.closedContacts)
  const newProject = useWorkbenchStore((state) => state.newProject)
  const loadProject = useWorkbenchStore((state) => state.loadProject)
  const setProjectIdentity = useWorkbenchStore((state) => state.setProjectIdentity)
  const markSaved = useWorkbenchStore((state) => state.markSaved)
  const setReadings = useWorkbenchStore((state) => state.setReadings)
  const setSimulationStatus = useWorkbenchStore((state) => state.setSimulationStatus)
  const deleteSelected = useWorkbenchStore((state) => state.deleteSelected)
  const undo = useWorkbenchStore((state) => state.undo)
  const redo = useWorkbenchStore((state) => state.redo)
  const setActiveTool = useWorkbenchStore((state) => state.setActiveTool)

  useEffect(() => {
    const isDark = theme === 'dark'
    globalThis.document.documentElement.dataset.theme = theme
    globalThis.document.body.classList.toggle('dark-mode', isDark)
    globalThis.document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', isDark ? '#171a18' : '#f4f3ea')
    localStorage.setItem(THEME_KEY, String(isDark))
  }, [theme])

  const saveMutation = useMutation({
    mutationFn: async ({ saveAsName }: { saveAsName?: string }) => {
      persistCurrentWorkspace()
      if (saveAsName || !projectId) {
        const name = saveAsName || document.projectName
        const response = await projectApi.create(name, { ...document, projectName: name })
        setProjectIdentity(response.projectId, response.projectName)
        return response
      }
      return projectApi.update(projectId, document.projectName, document)
    },
    onSuccess: () => {
      markSaved()
      try {
        persistCurrentWorkspace()
      } catch (cause) {
        console.error('Breadboard workspace persistence failed after cloud save', cause)
      }
      queryClient.invalidateQueries({ queryKey: ['breadboard-projects'] })
      setToast({ kind: 'ok', message: '项目已安全保存到云端' })
    },
    onError: (cause) => setToast({ kind: 'error', message: cause instanceof Error ? cause.message : '保存失败' }),
  })

  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY)
    const workspace = draft ?? localStorage.getItem(WORKSPACE_KEY)
    if (!workspace) {
      workspaceHydratedRef.current = true
      return
    }
    try {
      const parsed = JSON.parse(workspace) as PersistedWorkspace
      loadProject(parsed.projectId ?? 0, parseDocument(parsed.document))
      if (!parsed.projectId) useWorkbenchStore.setState({ projectId: null })
      useWorkbenchStore.setState({ dirty: parsed.dirty ?? Boolean(draft) })
      restoredWorkspaceRef.current = true
    } catch {
      localStorage.removeItem(DRAFT_KEY)
      localStorage.removeItem(WORKSPACE_KEY)
    } finally {
      workspaceHydratedRef.current = true
    }
  }, [loadProject])

  useEffect(() => {
    if (!restoredWorkspaceRef.current) return
    restoredWorkspaceRef.current = false
    queueMicrotask(() => setToast({ kind: 'ok', message: '已恢复上次的面包板工作区' }))
  }, [])

  useEffect(() => {
    if (!workspaceHydratedRef.current) return
    const timer = window.setTimeout(() => {
      try {
        persistCurrentWorkspace()
      } catch (cause) {
        console.error('Breadboard workspace persistence failed', cause)
      }
    }, 350)
    return () => window.clearTimeout(timer)
  }, [dirty, document, projectId])

  useEffect(() => {
    const persistBeforeLeaving = () => {
      if (!workspaceHydratedRef.current) return
      try {
        persistCurrentWorkspace()
      } catch (cause) {
        console.error('Breadboard workspace persistence failed before page unload', cause)
      }
    }
    window.addEventListener('pagehide', persistBeforeLeaving)
    return () => window.removeEventListener('pagehide', persistBeforeLeaving)
  }, [])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 3200)
    return () => window.clearTimeout(timer)
  }, [toast])

  const openCloudDialog = useCallback(async (mode: 'open' | 'saveAs') => {
    try {
      persistCurrentWorkspace()
      await requireCloudToken()
      setDialog(mode)
    } catch (cause) {
      setToast({ kind: 'error', message: cause instanceof Error ? cause.message : '无法打开云端项目' })
    }
  }, [])

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      const target = event.target as HTMLElement
      if (target.matches('input, textarea, select') || target.isContentEditable) return
      if (event.code === 'Space' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const state = useWorkbenchStore.getState()
        if (state.activeSensor) { event.preventDefault(); if (!event.repeat) state.rotateSensorPlacement(); return }
        const tool = state.activeTool
        if (tool === 'cd4017' || tool === 'cd4026' || tool === 'esp32-s3' || tool === 'seven-segment') {
          event.preventDefault()
          if (!event.repeat) state.rotatePlacement()
          return
        }
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && !useWorkbenchStore.getState().annotationMode) deleteSelected()
      if (event.key === 'Escape') {
        setActiveTool('select')
        if (canvasFullscreen) toggleCanvasFullscreen()
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        const state = useWorkbenchStore.getState()
        if (state.annotationMode) { if (event.shiftKey) state.redoAnnotation(); else state.undoAnnotation() }
        else if (event.shiftKey) redo()
        else undo()
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        if (projectId) saveMutation.mutate({})
        else void openCloudDialog('saveAs')
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [openCloudDialog, canvasFullscreen, deleteSelected, projectId, redo, saveMutation, setActiveTool, toggleCanvasFullscreen, undo])

  const requestNew = () => {
    if (!useWorkbenchStore.getState().dirty || window.confirm('新建项目会清空当前画布，未保存更改仍可从本地草稿恢复。继续吗？')) newProject()
  }

  return (
    <div className={`app-shell ${canvasFullscreen ? 'is-canvas-fullscreen' : ''}`}>
      <Toolbar
        onNew={requestNew}
        onOpen={() => { void openCloudDialog('open') }}
        onSave={() => { if (projectId) saveMutation.mutate({}); else void openCloudDialog('saveAs') }}
        onSaveAs={() => { void openCloudDialog('saveAs') }}
        saving={saveMutation.isPending}
        theme={theme}
        onToggleTheme={() => setTheme((current) => current === 'dark' ? 'light' : 'dark')}
      />
      <div className={`workspace-grid ${leftSidebarCollapsed ? 'is-left-collapsed' : ''} ${rightSidebarCollapsed ? 'is-right-collapsed' : ''}`}>
        <Palette />
        <BreadboardCanvas isDark={theme === 'dark'} isFullscreen={canvasFullscreen} onToggleFullscreen={toggleCanvasFullscreen} />
        <Inspector />
        <button
          type="button"
          className="sidebar-toggle sidebar-toggle-left"
          aria-label={leftSidebarCollapsed ? '展开元器件栏' : '折叠元器件栏'}
          title={leftSidebarCollapsed ? '展开元器件栏' : '折叠元器件栏'}
          onClick={() => setLeftSidebarCollapsed((collapsed) => !collapsed)}
        >
          {leftSidebarCollapsed ? <ChevronRight size={21} /> : <ChevronLeft size={21} />}
        </button>
        <button
          type="button"
          className="sidebar-toggle sidebar-toggle-right"
          aria-label={rightSidebarCollapsed ? '展开属性栏' : '折叠属性栏'}
          title={rightSidebarCollapsed ? '展开属性栏' : '折叠属性栏'}
          onClick={() => setRightSidebarCollapsed((collapsed) => !collapsed)}
        >
          {rightSidebarCollapsed ? <ChevronLeft size={21} /> : <ChevronRight size={21} />}
        </button>
      </div>

      <CircuitJsEngine document={document} closedContacts={closedContacts} running={running} onReadings={setReadings} onStatus={setSimulationStatus} />

      {dialog ? (
        <ProjectDialog
          mode={dialog}
          currentName={document.projectName}
          onClose={() => setDialog(null)}
          onOpen={loadProject}
          onSaveAs={(name) => saveMutation.mutateAsync({ saveAsName: name }).then(() => undefined)}
        />
      ) : null}
      {toast ? <div className={`toast toast-${toast.kind}`}>{toast.kind === 'ok' ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}{toast.message}</div> : null}
    </div>
  )
}
